import React, { useEffect, useMemo, useState } from 'react';
import { Descriptions } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGasPump } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DescriptionsItemType } from 'antd/lib/descriptions';
import isEqual from 'lodash.isequal';
import uuid from 'short-uuid';
import { CargoContractSides, CurrencySymbols, EstimateStatus } from '@shared/utils/constants';
import { EstimateBunker } from '@api/features/estimates/getEstimateDetails';
import Card from '@client/components/Card/Card';
import NumericInput from '@client/components/NumericInput';
import Button from '@client/components/Button';
import Modal from '@client/components/Modal/Modal';
import { useEstimate } from '../context/EstimatorContext';
import { useEstimateChangesProvider } from '../context/EstimateChangesContext';
import { getCommencementBunkerValues } from '../helpers/getCommencementBunkerValues';
import styles from './styles/EstimateConfigurationCard.module.css';
import RobsAndPricesModal from './RobsAndPricesModal';

const RobsAndPrices = () => {
	const [showModal, setShowModal] = useState(false);

	// Used for resetting commencement bunkers in case the user closes the 
	// ROB modal without saving
	const [originalRobValues, setOriginalRobValues] = useState<Array<EstimateBunker> | undefined>();

	const {
		estimate,
		onUpdateEstimate,
	} = useEstimate();

	const {
		saving,
		onSaveChanges,
		hasPendingChanges,
		onClearPendingChanges,
		useBlocker,
		makeBlockable,
	} = useEstimateChangesProvider();
	
	useBlocker(showModal && hasPendingChanges, 'You have unsaved changes. Are you sure you want to close?');

	const onCloseModal = makeBlockable(() => {
		setShowModal(false);
		onUpdateEstimate({
			// Remove ID
			commencementBunkers: originalRobValues?.map((r) => ({
				quantity: r.quantity,
				pricePerTon: r.pricePerTon,
				fuelGrade: r.fuelGrade
			}))
		});
		onClearPendingChanges();
	});

	// Synchronize local bunkers in case the estimate changes elsewhere
	useEffect(() => {
		if (
			originalRobValues &&
			estimate?.commencementBunkers &&
			!hasPendingChanges &&
			!isEqual(originalRobValues, estimate?.commencementBunkers)
		) {
			setOriginalRobValues(estimate?.commencementBunkers.map((c) => ({
				...c,
				id: uuid.generate(),
			})));
		}

		if (originalRobValues || !estimate?.commencementBunkers) {
			return;
		}

		setOriginalRobValues(estimate.commencementBunkers.map((b) => ({
			...b,
			id: uuid.generate(),
		})));
	}, [estimate?.commencementBunkers]);

	const isFixed = estimate?.status === EstimateStatus.Fixed;
	const consumption = estimate?.bunkerExpenses ?? {};
	const hasCargoRelet = estimate?.cargos.some(
		(cargo) => cargo.contractSide === CargoContractSides.VC_IN
	);

	const onSave = () => {
		onSaveChanges();
		setOriginalRobValues(undefined);
	};

	const bunkerValues = useMemo(
		() => getCommencementBunkerValues(estimate?.commencementBunkers),
		[estimate?.commencementBunkers],
	);

	const items = useMemo(() => {
		const cols: DescriptionsItemType[] = [];

		bunkerValues.forEach((bunkerValue) => {
			const disabled = bunkerValue.numberOfParcels > 1;
			const price = {
				className: disabled && styles.calc,
				label: `${bunkerValue.fuelGrade} price`,
				span: 1,
				children: (
					<NumericInput
						variant="borderless"
						highlightOnNull
						disabled={isFixed || hasCargoRelet || disabled}
						data-shortcut="bunker-price"
						value={bunkerValue.weightedPricePerTon ?? 0}
						addonBefore={CurrencySymbols[estimate?.currency as keyof typeof CurrencySymbols] ?? estimate?.currency}
						addonAfter="/MT"
						onChange={(e) => {
							const commencementBunkers = estimate?.commencementBunkers.map((estimateBunker) => {
								if (estimateBunker.fuelGrade === bunkerValue.fuelGrade) {
									const res = {
										...estimateBunker,
										pricePerTon: e ?? 0,
									};

									delete res.id;

									return res;
								}

								return estimateBunker;
							});

							onUpdateEstimate({
								commencementBunkers,
							});
						}}
					/>
				),
			};

			const qty = {
				className: disabled && styles.calc,
				label: `${bunkerValue.fuelGrade} qty`,
				span: 1,
				children: (
					<NumericInput
						variant="borderless"
						highlightOnNull
						disabled={isFixed || hasCargoRelet || disabled}
						value={bunkerValue.totalQuantity ?? 0}
						addonAfter="MT"
						onChange={(e) => {
							const commencementBunkers = estimate?.commencementBunkers.map((estimateBunker) => {
								if (estimateBunker.fuelGrade === bunkerValue.fuelGrade) {
									const res = {
										...estimateBunker,
										quantity: e ?? 0,
									};

									delete res.id;

									return res;
								}

								return estimateBunker;
							});

							onUpdateEstimate({
								commencementBunkers,
							});
						}}
					/>
				),
			};

			cols.push(price);
			cols.push(qty);
		});
		
		return cols;
	}, [bunkerValues, consumption, isFixed, hasCargoRelet]);

	if (bunkerValues.length === 0) {
		return null;
	}

	return (
		<>
			<Modal
				title="ROBs & prices"
				open={showModal}
				onCancel={onCloseModal}
				onOk={() => setShowModal(false)}
				cancelButtonProps={{
					style: {
						visibility: 'hidden',
					},
				}}
				okButtonProps={{
					style: {
						visibility: 'hidden',
					},
				}}
				width={600}
			>
				{estimate && (
					<RobsAndPricesModal
						onUpdateEstimate={onUpdateEstimate}
						estimate={estimate}
						onSaveChanges={() => {
							onSave();
							setShowModal(false);
						}}
						saving={saving}
						hasPendingChanges={hasPendingChanges}
					/>)}
			</Modal>
			<Card
				size="small"
				className={styles.card}
				styles={{ body: { padding: 0 } }}
				extra={(
					<Button
						size="small"
						type="primary"
						icon={(<FontAwesomeIcon icon={faGasPump as IconProp} />)}
						onClick={() => setShowModal(true)}
					>
						Set ROBs & prices
					</Button>
				)}
			>
				<Descriptions 
					column={4}
					className={styles.desc} 
					size="small" 
					bordered 
					items={items} 
				/>
			</Card>
		</>
	);
};

export default RobsAndPrices;
